// Hook custom per recuperare province e comuni di un'agenzia tramite API.
// Nota: uso axios e TypeScript per tipizzare la risposta e gestire errori e loading.

import { useState, useEffect } from 'react';
import axios from 'axios';
import { AddressResponse } from '../types/address';

// Prendo la base URL dell'API dalle variabili d'ambiente
const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

/**
 * useGetAddresses
 * @param agencyId - l'ID dell'agenzia per cui vogliamo recuperare province/comuni
 * @returns { data, loading, error }
 *
 * Nota per il prossimo dev:
 * - `data` contiene la risposta tipizzata come AddressResponse | null
 * - `loading` è true mentre la chiamata è in corso
 * - `error` contiene un messaggio di errore in caso di fallimento
 */
export const useGetAddresses = (agencyId: number) => {
  const [data, setData] = useState<AddressResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Funzione auto-invocata async per fare la chiamata API
    (async () => {
      // Se non ho agencyId, esco subito
      if (!agencyId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true); // inizio loading
        setError(null);   // resetto eventuali errori precedenti

        // Chiamata POST all'endpoint per recuperare province/comuni
        const response = await axios.post<AddressResponse>(
          `${API_BASE_URL}/province-comuni-agency`,
          { agencyId }
        );

        // salvo la risposta nello state
        setData(response.data);
      } catch (err) {
        setError('Errore durante il recupero degli indirizzi');
        console.error("Errore in useGetAddresses:", err);
      } finally {
        setLoading(false); // fine loading
      }
    })();
  }, [agencyId]); // richiamo ogni volta che cambia agencyId

  return { data, loading, error };
};

export default useGetAddresses;
